import type {
  MailcomDrainingActivity,
  MailcomDrainingReason,
  MailcomSupplyResponse,
} from '../../api/endpoints.js';

/**
 * Shape guard for the supply document. An older API build, a proxy error page
 * or any other 200 that is not the supply document must render as an error
 * state, never crash the page on an undefined field.
 */
export function isMailcomSupply(v: unknown): v is MailcomSupplyResponse {
  if (typeof v !== 'object' || v === null) return false;
  const d = v as Record<string, unknown>;
  const obj = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null;
  const draining = d['draining'];
  return (
    obj(d['cap'])
    && obj(d['active'])
    && obj(d['all'])
    && obj(d['pace'])
    && obj(d['estimate'])
    && obj(draining)
    && Array.isArray(draining['rows'])
    && obj(draining['byReason'])
  );
}

/**
 * WS5b - Turkish copy + pure formatters for the mail.com supply view
 * (Q64 / Q66 / Q67). No React here so the strings are unit-tested directly.
 */

const NUM = (n: number, max = 1): string =>
  n.toLocaleString('tr-TR', { maximumFractionDigits: max });

/** "yaklaşık X saat / Y gün" from hours; null = no recent consumption to divide by. */
export function formatSupplyEta(hours: number | null | undefined): string {
  if (hours === null || hours === undefined || !Number.isFinite(hours)) {
    return 'hesaplanamadı (son saatlerde tüketim yok)';
  }
  if (hours < 1) return 'yaklaşık 1 saatten az';
  const days = hours / 24;
  return `yaklaşık ${NUM(Math.round(hours), 0)} saat / ${NUM(days, 1)} gün`;
}

/** Whole-number count with Turkish grouping; null renders as an em dash. */
export function formatCount(n: number | null | undefined): string {
  return n === null || n === undefined ? '—' : n.toLocaleString('tr-TR');
}

/** "pace" line: aliases consumed in the window and the hourly rate. */
export function formatPace(consumed: number, perHour: number, windowHours: number): string {
  return `son ${windowHours} saatte ${formatCount(consumed)} alias tüketildi (≈ ${NUM(perHour, 0)}/saat)`;
}

/** "üretilen / kalan" cell text. */
export function formatCreatedRemaining(created: number | undefined, remaining: number | undefined): string {
  if (created === undefined || remaining === undefined) return '—';
  return `${created} / ${remaining}`;
}

export type CreatedRemainingTone = 'danger' | 'warn' | 'neutral';

/** Tone: no rights left = danger, near cap (>=80 created) = warn. */
export function createdRemainingTone(
  created: number | undefined,
  remaining: number | undefined,
  nearFrom = 80,
): CreatedRemainingTone {
  if (created === undefined || remaining === undefined) return 'neutral';
  if (remaining === 0) return 'danger';
  if (created >= nearFrom) return 'warn';
  return 'neutral';
}

export const DRAINING_REASON_LABEL: Record<MailcomDrainingReason, string> = {
  cap_reached: 'Limit doldu',
  near_cap: 'Limite yakın',
  stuck_below_cap: 'Limit altında takılı',
};

export const DRAINING_REASON_HINT: Record<MailcomDrainingReason, string> = {
  cap_reached:
    'En az 91 alias üretilmiş (ya da limit kuralıyla emekli edilmiş): mail.com ömür boyu alias limiti doldu, bu parent yeni alias üretemez.',
  near_cap:
    '80–90 alias üretilmiş: limite çok yakın, kalan hak 9–19; yenileme kısmi üretimle bitebilir.',
  stuck_below_cap:
    '80’in altında alias üretilmiş ama parent draining’de kalmış: limit sebep değil, kök neden araştırılmalı (giriş/oturum, kimlik, yenileme hatası).',
};

export const DRAINING_ACTIVITY_LABEL: Record<MailcomDrainingActivity, string> = {
  retired: 'Emekli (limit kuralı)',
  waiting_retry: 'Yeniden deneme bekliyor',
  frozen: 'Donmuş: yenileme işi yok',
  cycling: 'Takılı: yenileme deniyor',
};

export function describeActivityHint(activity: MailcomDrainingActivity, frozenAfterHours: number): string {
  switch (activity) {
    case 'retired':
      return 'Limit kuralı bu parent’ı kapattı (devre dışı); yeniden denenmez.';
    case 'waiting_retry':
      return 'Limit yeniden deneme aralığında bekliyor; sıradaki deneme zamanı gelene kadar normal yenileme döngüsüne girmez.';
    case 'frozen':
      return `Son ${frozenAfterHours} saatte bu parent için hiç yenileme işi çalışmadı: kimse denemiyor (donmuş).`;
    default:
      return `Yenileme işleri son ${frozenAfterHours} saat içinde çalışıyor ama parent draining’den çıkamıyor (takılı).`;
  }
}

/** "5 gün" / "bugün" for the since-when column. */
export function formatSinceDays(days: number | null | undefined): string {
  if (days === null || days === undefined) return '—';
  if (days <= 0) return 'bugün';
  return `${days} gün`;
}
